import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { X, Save, Loader2, BookOpen } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GENRES = [
  { id: 'memoir', name: 'Memoir', emoji: '📖' },
  { id: 'fiction', name: 'Fiction', emoji: '🌟' },
  { id: 'business', name: 'Business', emoji: '💼' },
  { id: 'childrens', name: "Children's", emoji: '🧒' },
  { id: 'freestyle', name: 'Freestyle', emoji: '✨' },
];

function EditBookModal({ book, onClose, onUpdate }) {
  const [title, setTitle] = useState(book.title);
  const [description, setDescription] = useState(book.description || '');
  const [genre, setGenre] = useState(book.genre);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!title.trim()) {
      toast.error('Please enter a title');
      return;
    }

    setSaving(true);
    try {
      const response = await axios.put(
        `${API}/books/${book.book_id}`,
        { title: title.trim(), description: description.trim(), genre },
        
      );
      
      toast.success('Book updated!');
      onUpdate(response.data);
      onClose();
    } catch (error) {
      console.error('Failed to update book:', error);
      toast.error('Failed to update book');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white dark:bg-[#22201C] rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-[#2A2520]/50">
          <div className="flex items-center space-x-3">
            <BookOpen className="w-6 h-6 text-[#7C766E]" />
            <h2 className="text-xl font-bold text-[#2A2420] dark:text-[#F0EBE4]">
              Edit Book
            </h2>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-5">
          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-[#2A2420] dark:text-[#F0EBE4] mb-2">
              Book Title
            </label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Enter book title"
              data-testid="edit-book-title"
              className="w-full"
            />
          </div>

          {/* Genre */}
          <div>
            <label className="block text-sm font-medium text-[#2A2420] dark:text-[#F0EBE4] mb-2">
              Genre
            </label>
            <div className="grid grid-cols-2 gap-2">
              {GENRES.map((g) => (
                <button
                  key={g.id}
                  type="button"
                  onClick={() => setGenre(g.id)}
                  className={`p-3 rounded-lg border-2 text-left transition-all ${
                    genre === g.id
                      ? 'border-[#8B7355] bg-[#8B7355]/10'
                      : 'border-gray-200 dark:border-[#2A2520] hover:border-[#8B7355]/50'
                  }`}
                >
                  <span className="text-lg mr-2">{g.emoji}</span>
                  <span className="text-sm font-medium text-[#2A2420] dark:text-[#F0EBE4]">{g.name}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-[#2A2420] dark:text-[#F0EBE4] mb-2">
              Description (optional)
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What's your book about?"
              rows={3}
              spellCheck={true}
              className="w-full px-3 py-2 border border-gray-300 dark:border-[#2A2520] rounded-lg bg-white dark:bg-[#2A2520] text-[#2A2420] dark:text-[#F0EBE4] focus:outline-none focus:ring-2 focus:ring-[#8B7355]"
              data-testid="edit-book-description"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end space-x-3 p-6 border-t border-gray-200 dark:border-[#2A2520]/50">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving || !title.trim()}
            className="bg-[#8B7355] hover:bg-[#7A6347] text-white"
            data-testid="save-book-button"
          >
            {saving ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Save Changes
          </Button>
        </div>
      </div>
    </div>
  );
}

export default EditBookModal;
